import React, { useState, useEffect } from "react";
import { Button, message, Popconfirm, Modal, Tag, Popover, Switch, Radio } from "antd";
import dayjs from "dayjs";
import AddShops from "../../component/AddShop";
import ShopServ from "../../service/ShopServ";
import CategoryServ from "../../service/CategoryServ";
import "../../css/shops.css";
import { useParams } from "react-router-dom";
import {
} from "@ant-design/icons";
import {
  IconSearch,
  IconPlus,
  IconRefresh,
  IconBuildingStore,
  IconX,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react";

import Breadcrumbs from "../../component/Breadcrumbs";
import { getImageUrl } from "../../component/SafeImage";
import DateRangePicker from "../../component/ui/DateRangePicker";
import FormSelect from "../../component/ui/FormSelect";
import DataTable from "../../component/ui/DataTable";
import TableImage from "../../component/ui/TableImage";

const Shop = () => {
  const { id } = useParams();


  const [shops, setShops] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [sortOrder, setSortOrder] = useState("newest");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [categories, setCategories] = useState([]);

  const [addShops, setAddShops] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [shopDetails, setShopDetails] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalShops, setTotalShops] = useState(0);

  const fetchShops = async () => {
    try {
      const response = await ShopServ.getSellerShops();
      const shopsData = response?.data || [];
      setShops(shopsData);
      setTotalShops(shopsData.filter(s => !(!s.isActive && s.approvalStatus === 'approved')).length);
    } catch (error) {
      console.error("Error fetching shops:", error);
      message.error("Failed to fetch shops");
    }
  };

  useEffect(() => { fetchShops(); }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await CategoryServ.index({ limit: "all" });
        let data = res?.data || res;
        if (!Array.isArray(data)) data = data.data || data.categories || data.items || [];
        setCategories(
          data.filter((c) => !c.parentId).map((c) => ({
            value: (c._id || c.id)?.toString(),
            label: c.name,
          }))
        );
      } catch {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  const filteredShops = shops
    .filter((shop) => {
      // Hide soft-deleted shops (approved but inactive) 
      if (!shop.isActive && shop.approvalStatus === 'approved') return false;
      let matches = true;
      if (searchText) {
        const q = searchText.toLowerCase();
        const inName = shop.name?.toLowerCase().includes(q);
        const inAddress = shop.location?.address?.toLowerCase().includes(q);
        const inCity = shop.location?.city?.toLowerCase().includes(q);
        if (!inName && !inAddress && !inCity) matches = false;
      }
      if (dateRange) {
        const shopDate = new Date(shop.updatedAt || shop.createdAt);
        const start = dateRange[0].startOf("day").toDate();
        const end = dateRange[1].endOf("day").toDate();
        if (shopDate < start || shopDate > end) matches = false;
      }
      if (categoryFilter) {
        const shopCatId = (
          shop.business?.categoryId?._id ||
          shop.business?.categoryId?.id ||
          shop.business?.categoryId
        )?.toString();
        if (shopCatId !== categoryFilter) matches = false;
      }
      return matches;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt).getTime();
      if (sortOrder === "newest") return dateB - dateA;
      if (sortOrder === "oldest") return dateA - dateB;
      if (sortOrder === "a-z") return (a.name || "").localeCompare(b.name || "");
      if (sortOrder === "z-a") return (b.name || "").localeCompare(a.name || "");
      return 0;
    });

  useEffect(() => {
    const fetchSingleShop = async (shopId) => {
      if (!shopId) return;
      try {
        const response = await ShopServ.single(shopId);
        setShopDetails(response?.data || response);
        setSelectedRow(response?.data || response);
      } catch (error) {
        console.error("Error fetching single shop:", error);
        message.error("Failed to fetch the shop");
      }
    };
    if (id) fetchSingleShop(id);
  }, [id]);

  const handleReset = () => {
    setSearchText("");
    setDateRange(null);
    setSortOrder("newest");
    setCategoryFilter(null);
  };

  const handleDeleteShop = async (id) => {
    try {
      await ShopServ.remove(id);
      message.success("Shop removed successfully");
      fetchShops();
      setSelectedRow(null);
    } catch (error) {
      console.error("Error deleting shop:", error);
      message.error(error.message || "Failed to remove shop");
    }
  };
  // ✅ Shop Open/Close Toggling and Temporary Close Modal State
  const [closeModalVisible, setCloseModalVisible] = useState(false);
  const [selectedShopForClose, setSelectedShopForClose] = useState(null);
  const [closeDuration, setCloseDuration] = useState("2"); // default 2 hours

  const handleShopOpenToggle = async (checked, record) => {
    if (checked) {
      // Reopen immediately
      try {
        await ShopServ.update(record._id, {
          business: {
            isOpen: true,
            closedUntil: null,
            isTemporarilyClosed: false,
            reopenTimeUnknown: false,
            manualClosedBySeller: false,
            closedReason: null
          }
        });
        message.success(`"${record.name}" has been successfully reopened!`);
        fetchShops();
      } catch (error) {
        message.error("Failed to reopen the shop");
      }
    } else {
      // Show duration modal to select how long to close
      setSelectedShopForClose(record);
      setCloseDuration("2"); // Reset to 2 hours default
      setCloseModalVisible(true);
    }
  };

  const handleConfirmClose = async () => {
    if (!selectedShopForClose) return;

    let reopenTime = null;
    const now = dayjs();

    let reopenTimeUnknown = false;

    if (closeDuration === "2") {
      reopenTime = now.add(2, "hour").toDate();
    } else if (closeDuration === "4") {
      reopenTime = now.add(4, "hour").toDate();
    } else if (closeDuration === "tomorrow") {
      let openHour = 9;
      let openMinute = 0;

      if (selectedShopForClose?.business?.openingTime) {
        const parts = selectedShopForClose.business.openingTime.split(':');
        if (parts.length >= 2) {
          openHour = parseInt(parts[0], 10) || 9;
          openMinute = parseInt(parts[1], 10) || 0;
        }
      }

      reopenTime = now.add(1, "day").hour(openHour).minute(openMinute).second(0).millisecond(0).toDate();
    } else if (closeDuration === "7days") {
      reopenTime = now.add(7, "day").startOf("day").toDate();
    } else if (closeDuration === "unknown") {
      reopenTime = null;
      reopenTimeUnknown = true;
    }

    try {
      await ShopServ.update(selectedShopForClose._id, {
        business: {
          isOpen: false,
          closedUntil: reopenTime,
          isTemporarilyClosed: true,
          reopenTimeUnknown: reopenTimeUnknown,
          manualClosedBySeller: true,
          closedAt: now.toDate()
        }
      });

      let durationText = "";
      if (closeDuration === "2") durationText = "2 Hours";
      else if (closeDuration === "4") durationText = "4 Hours";
      else if (closeDuration === "tomorrow") durationText = "Tomorrow Morning";
      else if (closeDuration === "7days") durationText = "7 Days";
      else if (closeDuration === "unknown") durationText = "an unknown time";

      message.success(`"${selectedShopForClose.name}" is now temporarily closed for ${durationText}.`);
      setCloseModalVisible(false);
      fetchShops();
    } catch (error) {
      message.error("Failed to temporarily close the shop");
    }
  };

  const columns = [
    {
      title: "Shop",
      key: "shop",
      render: (_, record) => (
        <div className="ss-shop-cell">
          <div className="ss-shop-img-wrap">
            <TableImage src={getImageUrl(record.imageUrl)} alt="shop" />
          </div>
          <div className="ss-shop-info">
            <span className="ss-shop-name">{record.name}</span>
            <span className="ss-shop-id">#{record.shopId || "—"}</span>
          </div>
        </div>
      ),
    },

    {
      title: "Category",
      key: "category",
      render: (_, record) => (
        <span className="ss-cat-badge">
          {record.business?.category || "—"}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 150,
      render: (_, record) => {
        // Self-contained badge styles — immune to Ant Design theme overrides
        const badgeStyles = {
          approved: {
            background: "#dcfce7",
            color: "#15803d",
            border: "1px solid #86efac",
          },
          rejected: {
            background: "#fee2e2",
            color: "#dc2626",
            border: "1px solid #fca5a5",
          },
          pending: {
            background: "#fff7ed",
            color: "#c2410c",
            border: "1px solid #fed7aa",
          },
        };

        const statusKey = record.approvalStatus || "pending";
        const labelMap = { approved: "Approved", rejected: "Rejected", pending: "Pending" };
        const style = badgeStyles[statusKey] || badgeStyles.pending;
        const label = labelMap[statusKey] || "Pending";

        const isResubmitted =
          record.approvalStatus === "pending" &&
          record.approvalHistory?.some((h) => h.status === "rejected");

        const badge = (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "4px 11px",
              borderRadius: "999px",
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.3px",
              lineHeight: 1.5,
              cursor: record.approvalStatus === "rejected" && record.rejectionReason ? "help" : "default",
              ...style,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: style.color,
                flexShrink: 0,
              }}
            />
            {label}
          </span>
        );

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {record.approvalStatus === "rejected" && record.rejectionReason ? (
              <Popover
                title={<span style={{ fontWeight: 700, color: "#dc2626" }}>❌ Rejection Reason</span>}
                content={
                  <div style={{ maxWidth: 260, fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
                    {record.rejectionReason}
                    <div style={{ marginTop: 8, fontSize: 11, color: "#6b7280" }}>
                      Click <strong>Edit &amp; Resubmit</strong> in the shop details to fix and resubmit.
                    </div>
                  </div>
                }
                trigger="hover"
              >
                {badge}
              </Popover>
            ) : (
              badge
            )}
            {isResubmitted && (
              <span
                style={{
                  fontSize: "9px",
                  fontWeight: 800,
                  color: "#2563eb",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                ↑ RESUBMITTED
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Hours",
      key: "hours",
      render: (_, record) => {
        const open = record.business?.openingTime
          ? dayjs(record.business.openingTime, "HH:mm:ss").format("hh:mm A")
          : null;
        const close = record.business?.closingTime
          ? dayjs(record.business.closingTime, "HH:mm:ss").format("hh:mm A")
          : null;
        if (!open && !close) return <span className="ss-muted">—</span>;
        return (
          <span className="ss-hours-badge">
            {open} – {close}
          </span>
        );
      },
    },
    {
      title: "Store Status",
      key: "isOpen",
      width: 140,
      align: "center",
      render: (_, record) => {
        const isOpen = record.business?.isOpen !== false;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={isOpen}
              onChange={(checked) => handleShopOpenToggle(checked, record)}
              checkedChildren="OPEN"
              unCheckedChildren="CLOSED"
              disabled={record.approvalStatus !== "approved"}
            />
          </div>
        );
      }
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   align: "right",
    //   render: (_, record) => (
    //     <div className="ss-actions">
    //       <button
    //         className="ss-btn-icon ss-btn-view"
    //         title="View"
    //         onClick={(e) => {
    //           e.stopPropagation();
    //           setShopDetails(record);
    //           setDetailsModalVisible(true);
    //         }}
    //       >
    //         <IconEye size={15} />
    //       </button>
    //       <button
    //         className="ss-btn-icon ss-btn-edit"
    //         title="Edit"
    //         onClick={(e) => {
    //           e.stopPropagation();
    //           handleEditShops(record);
    //         }}
    //       >
    //         <IconEdit size={15} />
    //       </button>
    //       <Popconfirm
    //         title="Remove this shop?"
    //         description="This action cannot be undone."
    //         onConfirm={(e) => { e?.stopPropagation(); handleDeleteShop(record._id); }}
    //         okText="Remove"
    //         cancelText="Cancel"
    //         okButtonProps={{ danger: true }}
    //       >
    //         <button
    //           className="ss-btn-icon ss-btn-delete"
    //           title="Remove"
    //           onClick={(e) => e.stopPropagation()}
    //         >
    //           <IconTrash size={15} />
    //         </button>
    //       </Popconfirm>
    //     </div>
    //   ),
    // },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setShopDetails(record);
            setDetailsModalVisible(true);
          }}
          style={{
            borderRadius: 3,
            fontWeight: 600,
            background: '#ffffffff',
            color: '#004754ff',
            border: '1px solid #e4e4e7',
            boxShadow: '0 0 0 1px #e4e4e7',
            height: 'auto',
            padding: '0px 8px',
            fontSize: 13,
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const handleCreateShops = () => {
    setIsEditing(false);
    setShopDetails(null);
    setAddShops(true);
  };

  const handleEditShops = (record) => {
    const target = record || selectedRow;
    if (!target) return;
    setShopDetails(target);
    setIsEditing(true);
    setAddShops(true);
  };

  const refreshShops = async (isRefresh) => {
    setAddShops(false);
    if (isRefresh) fetchShops();
  };

  return (
    <div className="ss-wrapper">
      <div className="pcu-margin-bottom-16">
        <Breadcrumbs />
      </div>

      <div className="ss-container">
        {/* Header */}
        <div className="ss-header">
          <div className="ss-header-left">
            <div className="ss-header-icon">
              <IconBuildingStore size={20} color="#4776E6" />
            </div>
            <div>
              <h1 className="ss-title">Shop Setup</h1>
              <p className="ss-subtitle">{totalShops} shop{totalShops !== 1 ? "s" : ""} registered</p>
            </div>
          </div>
          <button className="ss-create-btn" onClick={handleCreateShops}>
            <IconPlus size={16} />
            Create Shop
          </button>
        </div>

        {/* Filter Bar */}
        <div className="ss-filter-bar">
          {/* Search */}
          <div className="ss-filter-field">
            <label className="ss-filter-label">Search</label>
            <div className="ss-search-wrap">
              <IconSearch size={14} className="ss-search-icon" />
              <input
                className="ss-search-input"
                placeholder="Search by name or location..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {/* Date Range */}
          <div className="ss-filter-field">
            <label className="ss-filter-label">Updated Date</label>
            <DateRangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
            />
          </div>

          {/* Category */}
          <div className="ss-filter-field">
            <label className="ss-filter-label">Category</label>
            <FormSelect
              placeholder="All Categories"
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={categories}
              showSearch
              allowClear
            />
          </div>

          {/* Sort */}
          <div className="ss-filter-field">
            <label className="ss-filter-label">Sort By</label>
            <FormSelect
              value={sortOrder}
              onChange={setSortOrder}
              options={[
                { value: "newest", label: "Newest First" },
                { value: "oldest", label: "Oldest First" },
                { value: "a-z", label: "Name (A–Z)" },
                { value: "z-a", label: "Name (Z–A)" },
              ]}
            />
          </div>

          {/* Reset */}
          <div className="ss-filter-reset">
            <button className="ss-reset-btn" onClick={handleReset}>
              <IconRefresh size={14} />
              Reset
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="ss-table-wrap">
          <DataTable
            columns={columns}
            data={filteredShops}
            rowKey={(record) => record._id}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: filteredShops.length,
              onChange: (p) => setPage(p),
              onShowSizeChange: (_, size) => setPageSize(size),
            }}
          />
        </div>
      </div>

      {addShops && (
        <AddShops
          open={addShops}
          refreshShops={refreshShops}
          shopDetails={shopDetails || {}}
          isEditing={isEditing}
        />
      )}

      {/* Shop Detail Modal */}
      <Modal
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={null}
        width={720}
        className="ss-modal"
        closeIcon={<IconX size={18} />}
        styles={{ body: { padding: 0 } }}
      >
        {shopDetails && (
          <div className="ss-modal-body">
            {/* Modal Hero */}
            <div className="ss-modal-hero">
              <div className="ss-modal-img">
                <TableImage src={getImageUrl(shopDetails.imageUrl)} alt={shopDetails.name} size={80} radius={14} />
              </div>
              <div className="ss-modal-info">
                <h2 className="ss-modal-name">{shopDetails.name}</h2>
                <p className="ss-modal-id">Shop ID: <strong>{shopDetails.shopId || "—"}</strong></p>
                <div className="ss-modal-tags">
                  {shopDetails.business?.category && (
                    <span className="ss-tag ss-tag-orange">{shopDetails.business.category}</span>
                  )}
                  {shopDetails.business?.subCategoryId?.name && (
                    <span className="ss-tag ss-tag-blue">{shopDetails.business.subCategoryId.name}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Detail Grid */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px",
              padding: "16px 24px"
            }}>
              {/* Location - Full Width */}
              <div style={{ gridColumn: "1 / -1", background: "#f8fafc", borderRadius: "8px", padding: "8px 12px", border: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Location</div>
                <div style={{ fontSize: "13px", color: "#374151", fontWeight: 500 }}>
                  {shopDetails.location
                    ? `${shopDetails.location.address || ""}, ${shopDetails.location.city || ""}, ${shopDetails.location.state || ""} ${shopDetails.location.zipCode || ""}`
                    : "No location provided"}
                </div>
              </div>

              {/* Opening & Closing Time */}
              <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "8px 12px", border: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Opening Time</div>
                <div style={{ fontSize: "13px", color: "#16a34a", fontWeight: 600 }}>
                  {shopDetails.business?.openingTime
                    ? dayjs(shopDetails.business.openingTime, "HH:mm:ss").format("hh:mm A")
                    : "N/A"}
                </div>
              </div>
              <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "8px 12px", border: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Closing Time</div>
                <div style={{ fontSize: "13px", color: "#dc2626", fontWeight: 600 }}>
                  {shopDetails.business?.closingTime
                    ? dayjs(shopDetails.business.closingTime, "HH:mm:ss").format("hh:mm A")
                    : "N/A"}
                </div>
              </div>

              {/* Phone & Email */}
              <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "8px 12px", border: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Phone</div>
                <div style={{ fontSize: "13px", color: "#374151", fontWeight: 500 }}>
                  {shopDetails.contact?.phone || "N/A"}
                </div>
              </div>
              <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "8px 12px", border: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Email</div>
                <div style={{ fontSize: "13px", color: "#374151", fontWeight: 500 }}>
                  {shopDetails.contact?.email || "N/A"}
                </div>
              </div>

              {/* GST & License Number */}
              <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "8px 12px", border: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>GST Number</div>
                <div style={{ fontSize: "13px", color: "#374151", fontWeight: 500 }}>
                  {shopDetails.business?.gstNumber || "N/A"}
                </div>
              </div>
              <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "8px 12px", border: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Shop License Number</div>
                <div style={{ fontSize: "13px", color: "#374151", fontWeight: 500 }}>
                  {shopDetails.business?.shopLicenseNumber || "N/A"}
                </div>
              </div>

              {/* Status & Approval/Rejection Message */}
              <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "8px 12px", border: "1px solid #f1f5f9", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "4px", letterSpacing: "0.5px" }}>Status</div>
                <Tag
                  color={
                    shopDetails.approvalStatus === "approved"
                      ? "green"
                      : shopDetails.approvalStatus === "rejected"
                        ? "red"
                        : "orange"
                  }
                  style={{ margin: 0, fontWeight: 700 }}
                >
                  {(shopDetails.approvalStatus || "pending").toUpperCase()}
                </Tag>
              </div>

              {/* Storefront Open/Closed Status */}
              <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "8px 12px", border: "1px solid #f1f5f9", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "4px", letterSpacing: "0.5px" }}>Storefront Status</div>
                {shopDetails.business?.isOpen !== false ? (
                  <Tag color="success" style={{ margin: 0, fontWeight: 700 }}>OPEN</Tag>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <Tag color="error" style={{ margin: 0, fontWeight: 700, width: "fit-content" }}>CLOSED TEMPORARILY</Tag>
                    {shopDetails.business?.closedUntil && (
                      <span style={{ fontSize: "10px", color: "#64748b", fontWeight: 600 }}>
                        Reopens: {dayjs(shopDetails.business.closedUntil).format("MMM DD, hh:mm A")}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {shopDetails.approvalStatus === "approved" && (() => {
                const lastApproval = shopDetails.approvalHistory?.slice().reverse().find(h => h.status === "approved");
                const msg = lastApproval?.reason;
                if (msg && msg !== "Shop approved by admin") {
                  return (
                    <div style={{ background: "#f0fdf4", borderRadius: "8px", padding: "8px 12px", border: "1px solid #dcfce7" }}>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "#16a34a", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Approval Message</div>
                      <div style={{ fontSize: "13px", color: "#16a34a", fontWeight: 600 }}>{msg}</div>
                    </div>
                  );
                }
                return <div />;
              })()}

              {shopDetails.approvalStatus === "rejected" && (
                <div style={{ background: "#fff1f2", borderRadius: "8px", padding: "8px 12px", border: "1px solid #fecdd3" }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: "#e11d48", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Rejection Reason</div>
                  <div style={{ fontSize: "13px", color: "#e11d48", fontWeight: 600 }}>{shopDetails.rejectionReason || "No reason provided"}</div>
                </div>
              )}

              {/* Description - Full Width */}
              <div style={{ gridColumn: "1 / -1", background: "#f8fafc", borderRadius: "8px", padding: "8px 12px", border: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Description</div>
                <div style={{ fontSize: "13px", color: "#374151", fontWeight: 500, lineHeight: "1.5" }}>
                  {shopDetails.description || "No description provided"}
                </div>
              </div>
            </div>


            <div className="ss-modal-footer" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 10 }}>
                {shopDetails.approvalStatus === "rejected" ? (
                  <button
                    className="ss-modal-close-btn"
                    style={{
                      background: "linear-gradient(135deg, #ef4444, #dc2626)",
                      color: "#fff",
                      border: "none",
                      fontWeight: 700,
                      boxShadow: "0 2px 8px rgba(239,68,68,0.35)"
                    }}
                    onClick={() => {
                      setDetailsModalVisible(false);
                      handleEditShops(shopDetails);
                    }}
                  >
                    <IconEdit size={14} style={{ marginRight: 6 }} /> Edit & Resubmit
                  </button>
                ) : (
                  <button
                    className="ss-modal-close-btn"
                    style={{ background: "#e6f4ff", color: "#0958d9", border: "1px solid #91d5ff" }}
                    onClick={() => {
                      setDetailsModalVisible(false);
                      handleEditShops(shopDetails);
                    }}
                  >
                    <IconEdit size={14} style={{ marginRight: 6 }} /> Edit Shop
                  </button>
                )}

                <Popconfirm
                  title="Remove this shop?"
                  description="All products in this shop will be hidden. This action is permanent."
                  onConfirm={() => {
                    handleDeleteShop(shopDetails._id);
                    setDetailsModalVisible(false);
                  }}
                  okText="Remove"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <button
                    className="ss-modal-close-btn"
                    style={{ background: "#fff1f2", color: "#e11d48", border: "1px solid #fecdd3" }}
                  >
                    <IconTrash size={14} style={{ marginRight: 6 }} /> Remove </button>
                </Popconfirm>
              </div>

              <button className="ss-modal-close-btn" onClick={() => setDetailsModalVisible(false)}>
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
      {/* Temporary Closure Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0', marginBottom: '16px' }}>
            <div style={{ width: '40px', height: '40px', background: '#6366f1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconBuildingStore size={20} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '16px', color: '#1e293b', lineHeight: 1, marginBottom: '4px' }}>Temporarily Close Shop</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Select a duration to pause new orders</div>
            </div>
          </div>
        }
        open={closeModalVisible}
        onCancel={() => setCloseModalVisible(false)}
        onOk={handleConfirmClose}
        okText="Confirm Closure"
        cancelText="Cancel"
        centered
        width={480}
        closeIcon={<IconX size={20} color="#94a3b8" />}
        okButtonProps={{
          style: {
            background: "var(--nearzon-orange, #f97316)",
            border: "none",
            fontWeight: 700,
            borderRadius: "6px",
            height: "38px",
            padding: "0 24px"
          }
        }}
        cancelButtonProps={{
          style: {
            borderRadius: "6px",
            fontWeight: 600,
            height: "38px",
            padding: "0 24px",
            color: "#64748b",
            border: "1px solid #cbd5e1"
          }
        }}
      >
        <div style={{ padding: "0" }}>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px", lineHeight: "1.5", display: 'none' }}>
            Choose a duration for which your shop will be closed. It will automatically reopen once the selected time is up.
          </p>
          {(() => {
            let displayOpeningTime = "09:00 AM";
            if (selectedShopForClose?.business?.openingTime) {
              const parts = selectedShopForClose.business.openingTime.split(':');
              if (parts.length >= 2) {
                let hour = parseInt(parts[0], 10) || 9;
                let minute = parseInt(parts[1], 10) || 0;
                const ampm = hour >= 12 ? 'PM' : 'AM';
                hour = hour % 12 || 12;
                displayOpeningTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${ampm}`;
              }
            }
            return (
              <Radio.Group
                value={closeDuration}
                onChange={(e) => setCloseDuration(e.target.value)}
                style={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}
              >
                <Radio.Button value="2" style={{ height: "42px", display: "flex", alignItems: "center", borderRadius: "8px" }}>
                  <span style={{ fontWeight: 600 }}>2 Hours</span> <span style={{ fontSize: "11px", color: "#94a3b8", marginLeft: "8px" }}>(Quick break)</span>
                </Radio.Button>
                <Radio.Button value="4" style={{ height: "42px", display: "flex", alignItems: "center", borderRadius: "8px" }}>
                  <span style={{ fontWeight: 600 }}>4 Hours</span> <span style={{ fontSize: "11px", color: "#94a3b8", marginLeft: "8px" }}>(Half day)</span>
                </Radio.Button>
                <Radio.Button value="tomorrow" style={{ height: "42px", display: "flex", alignItems: "center", borderRadius: "8px" }}>
                  <span style={{ fontWeight: 600 }}>Tomorrow Morning</span> <span style={{ fontSize: "11px", color: "#94a3b8", marginLeft: "8px" }}>(Reopens at {displayOpeningTime})</span>
                </Radio.Button>
                <Radio.Button value="7days" style={{ height: "42px", display: "flex", alignItems: "center", borderRadius: "8px" }}>
                  <span style={{ fontWeight: 600 }}>After 7 Days</span> <span style={{ fontSize: "11px", color: "#94a3b8", marginLeft: "8px" }}>(Vacation Mode)</span>
                </Radio.Button>
                <Radio.Button value="unknown" style={{ height: "42px", display: "flex", alignItems: "center", borderRadius: "8px" }}>
                  <span style={{ fontWeight: 600 }}>Reopen time not sure</span> <span style={{ fontSize: "11px", color: "#94a3b8", marginLeft: "8px" }}>(Close until manual reopen)</span>
                </Radio.Button>
              </Radio.Group>
            );
          })()}
        </div>
      </Modal>
    </div>
  );
};

export default Shop;
